import { Box, Flex } from "@chakra-ui/react";
import React, { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useLandingPage } from "../../hooks/useLandingPage";
import ConsumerStepper from "../../shared/components/ConsumerStepper";
import ProfileTabs from "./ProfileTabs";

/**
 * Profile Layout Component
 * Handles the transition between stepper (onboarding) and tabs (completed) modes
 */
export default function ProfileLayout() {
  const { isOnboardingComplete, isLoading, getActiveTabFromPath, tabConfig } =
    useLandingPage();

  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = getActiveTabFromPath(location.pathname);

  // Handle navigation between tab and stepper modes (only for profile routes)
  useEffect(() => {
    if (isLoading) return;

    const currentPath = location.pathname;

    // Only handle profile-related routes
    if (!currentPath.startsWith('/me/profile/')) return;

    // If user is not onboarding complete, redirect to stepper routes
    if (!isOnboardingComplete) {
      let stepperRoute = "/me";

      if (currentPath.includes("/work-history")) {
        stepperRoute = "/me/work12";
      } else if (currentPath.includes("/education")) {
        stepperRoute = "/me/edu";
      } else if (currentPath.includes("/summary")) {
        stepperRoute = "/me/summary";
      }

      navigate(stepperRoute, { replace: true });
    }
  }, [isOnboardingComplete, isLoading, location.pathname, navigate]);

  const handleTabChange = (tabIndex) => {
    const route = isOnboardingComplete
      ? tabConfig[tabIndex]?.route
      : tabConfig[tabIndex]?.stepperRoute;

    if (route) {
      navigate(route);
    }
  };

  if (isLoading) {
    return (
      <Flex direction="column" w="100%" h="100vh">
        {/* Don't show stepper during loading to prevent double stepper flash */}
        <Box flex="1" overflow="auto">
          <Outlet />
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction="column" w="100%" h="100vh">
      {/* Navigation Header */}
      <Box flexShrink={0}>
        {isOnboardingComplete ? (
          <ProfileTabs activeTab={activeTab} onTabChange={handleTabChange} />
        ) : (
          <ConsumerStepper currentStep={activeTab} />
        )}
      </Box>

      {/* Content Area */}
      <Box flex="1" overflow="auto">
        <Outlet />
      </Box>
    </Flex>
  );
}
